import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { msalInstance, protectedResourceMap, loginRequest } from '@core/auth/msal.config';

/**
 * The portal's fetch wrapper, and the one place bearer tokens are attached.
 *
 * Every call in the app goes through `http`: it matches the outgoing URL
 * against `protectedResourceMap` and acquires the right scopes before the
 * request leaves, with `resolveScopes` doing the prefix matching.
 *
 * Two behaviours are deliberate:
 *
 *  - Silent-first token acquisition, falling back to a redirect when MSAL says
 *    interaction is required. That is what keeps an expired-token page load
 *    from dead-ending on a 401.
 *
 *  - Errors are thrown, not returned, so call sites can use try/catch rather
 *    than checking every result. `HttpError` keeps `status` and the parsed
 *    body reachable for the handlers that need them.
 */

export class HttpError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly body: unknown;

  constructor(response: Response, body: unknown) {
    super(`HTTP ${response.status} ${response.statusText} - ${response.url}`);
    this.name = 'HttpError';
    this.status = response.status;
    this.statusText = response.statusText;
    this.url = response.url;
    this.body = body;
  }
}

/** Longest-prefix match against the protected resource map, as MSAL's interceptor did. */
function resolveScopes(url: string): string[] | null {
  let matched: string[] | null = null;
  let matchedLength = 0;

  for (const [resource, scopes] of protectedResourceMap) {
    if (url.startsWith(resource) && resource.length > matchedLength) {
      matched = scopes;
      matchedLength = resource.length;
    }
  }

  return matched;
}

/**
 * Acquires a token for `scopes`, silently where possible.
 *
 * Returns `null` rather than throwing when there is no signed-in account: the
 * caller is then free to issue the request unauthenticated, which is what the
 * handful of anonymous endpoints expect.
 */
export async function acquireToken(scopes: string[]): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  if (!account) {
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Matches the interceptor's redirect interaction type. This navigates
      // away, so nothing after it runs.
      await msalInstance.acquireTokenRedirect({ ...loginRequest, scopes, account });
      return null;
    }

    throw error;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Query string values. `null` / `undefined` entries are dropped, as `HttpParams` did. */
  params?: Record<string, string | number | boolean | null | undefined>;
  headers?: Record<string, string>;
  /** Overrides the scopes the protected-resource map would have chosen. */
  scopes?: string[];
  /** Skips token acquisition entirely. */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** `json` is the default; `blob` backs the file downloads, `text` the CSV endpoints. */
  responseType?: 'json' | 'blob' | 'text' | 'response';
  /**
   * Hands back the `Response` for failing statuses instead of throwing. Only
   * meaningful alongside `responseType: 'response'`.
   *
   * The Fannie Mae export needs a 422's error summary decoded as a Blob, the
   * same `responseType` the request asked for. A thrown `HttpError` cannot
   * offer that, because it has already read the body as text.
   */
  allowErrorResponse?: boolean;
}

function buildUrl(url: string, params?: RequestOptions['params']): string {
  if (!params) {
    return url;
  }

  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      search.append(key, String(value));
    }
  }

  const query = search.toString();

  if (!query) {
    return url;
  }

  return url.includes('?') ? `${url}&${query}` : `${url}?${query}`;
}

export async function http<T = unknown>(url: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    params,
    headers = {},
    scopes,
    anonymous = false,
    signal,
    responseType = 'json',
    allowErrorResponse = false
  } = options;

  const requestHeaders: Record<string, string> = { ...headers };

  if (!anonymous) {
    const resolved = scopes ?? resolveScopes(url);

    if (resolved) {
      const token = await acquireToken(resolved);

      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }
    }
  }

  // FormData sets its own multipart boundary - naming a content type here
  // would corrupt the upload, which is how the vault's file uploads break.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  if (body !== undefined && !isFormData && !requestHeaders['Content-Type']) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await fetch(buildUrl(url, params), {
    method,
    headers: requestHeaders,
    signal,
    body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body)
  });

  if (!response.ok) {
    if (allowErrorResponse && responseType === 'response') {
      return response as unknown as T;
    }

    // Parse the failure body where we can - the API returns problem details
    // that several screens surface verbatim in a toast.
    let errorBody: unknown = null;

    try {
      const text = await response.text();
      errorBody = text ? JSON.parse(text) : null;
    } catch {
      errorBody = null;
    }

    throw new HttpError(response, errorBody);
  }

  if (responseType === 'response') {
    return response as unknown as T;
  }

  if (responseType === 'blob') {
    return (await response.blob()) as unknown as T;
  }

  if (responseType === 'text') {
    return (await response.text()) as unknown as T;
  }

  // 204, and the empty 200s some endpoints return, have no body to parse.
  if (response.status === 204) {
    return null as unknown as T;
  }

  const text = await response.text();

  return (text ? JSON.parse(text) : null) as T;
}

export const api = {
  get: <T>(url: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    http<T>(url, { ...options, method: 'GET' }),
  post: <T>(url: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    http<T>(url, { ...options, method: 'POST', body }),
  put: <T>(url: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    http<T>(url, { ...options, method: 'PUT', body }),
  patch: <T>(url: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    http<T>(url, { ...options, method: 'PATCH', body }),
  delete: <T>(url: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    http<T>(url, { ...options, method: 'DELETE' })
};
